import { cn } from "@shared/lib/utils";
import { Button } from "@shared/ui/button";
import { Tooltip as RadixTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@shared/ui/tooltip";
import { Slider } from "@shared/ui/slider";
import { Switch } from "@shared/ui/switch";
import { format } from "date-fns";
import {
  Check,
  ChevronDown,
  Eye,
  EyeOff,
  GripVertical,
  HelpCircle,
  Loader2,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Controller, UseFormReturn, useFieldArray } from "react-hook-form";
import { createPortal } from "react-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  FormConfig,
  FormField,
  FormService,
  defaultFormClasses,
} from "../services/FormServices";
import FileUploadComponent from "./FileUploadComponent";
import { PhoneInputComponent } from "./PhoneInputComponent";
import PlaceholderModal from "./PlaceHolderModal";
import TextareaWithPlaceholder from "./TestAreaWithPlaceholder";
import TextWithPlaceholder from "./TextWithPlaceholder";
import { GenericSelect } from "./Select";

interface DynamicFormProps {
  fields: FormField[];
  form: UseFormReturn<any>;
  onSubmit?: (data: any) => void | Promise<void>;
  onReset?: () => void;
  loading?: boolean;
  config?: Partial<FormConfig>;
  className?: string;
  onFieldChange?: (fieldName: string, value: any) => void;
  style?: React.CSSProperties;
  renderCustomFooter?: () => React.ReactNode;
  gridColumns?: number;
  responsiveColumns?: {
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
  onVerificationStatusChange?: (
    fieldName: string,
    status: FormField["verificationStatus"],
  ) => void;
}

// Tooltip Component
interface TooltipProps {
  content: string;
  position?: "top" | "bottom" | "left" | "right";
  children: React.ReactNode;
  className?: string;
}

const Tooltip = ({
  content,
  position = "top",
  children,
  className = "",
}: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);

  const getPositionClasses = () => {
    const positionClasses = {
      top: "bottom-full left-0 mb-2",
      bottom: "top-full left-0 mt-2",
      left: "right-full top-1/2 transform -translate-y-1/2 mr-2",
      right: "left-full top-1/2 transform -translate-y-1/2 ml-2",
    };
    return positionClasses[position];
  };

  const getArrowClasses = () => {
    const arrowClasses = {
      top: "top-full left-4 border-l-transparent border-r-transparent border-b-transparent border-t-gray-200",
      bottom:
        "bottom-full left-4 border-l-transparent border-r-transparent border-t-transparent border-b-gray-200",
      left: "left-full top-1/2 transform -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-gray-200",
      right:
        "right-full top-1/2 transform -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-gray-200",
    };
    return arrowClasses[position];
  };

  return (
    <div className={`relative inline-block ${className}`}>
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onClick={() => setIsVisible(!isVisible)}
      >
        {children}
      </div>
      {isVisible && (
        <div
          className={`absolute z-50 px-3 py-2 text-sm text-gray-700 bg-white rounded-lg shadow-lg border border-gray-200 whitespace-normal ${getPositionClasses()}`}
          style={{
            boxShadow:
              "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
            minWidth: "280px",
            maxWidth: "320px",
            lineHeight: "1.4",
          }}
        >
          {content}
          <div className={`absolute w-0 h-0 border-4 ${getArrowClasses()}`} />
        </div>
      )}
    </div>
  );
};

// Multi-Select Dropdown Component
interface MultiSelectDropdownProps {
  name: string;
  control: any;
  options: Array<{
    value: string | number;
    label: string;
    disabled?: boolean;
    description?: string;
  }>;
  onChange?: (values: (string | number)[]) => void;
  onSearchTermChange?: (searchTerm: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  maxDisplayItems?: number;
  searchable?: boolean;
  clearable?: boolean;
  closeOnSelect?: boolean;
  error?: string;
  maxSelection?: number;
}

const MultiSelectDropdown = ({
  name,
  control,
  options = [],
  placeholder = "Select options...",
  disabled = false,
  className = "",
  maxDisplayItems = 3,
  searchable = true,
  clearable = true,
  closeOnSelect = false,
  onChange,
  onSearchTermChange,
  maxSelection,
}: MultiSelectDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const getFilteredOptions = () => {
    if (!searchable || !searchTerm) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  };

  const getBaseClasses = (isSelected: boolean) => {
    return cn(
      "relative w-full min-h-[44px] flex flex-nowrap overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] items-center gap-1 p-1 pr-14 border rounded-lg bg-white text-sm transition-all duration-200",
      "border-gray-300 hover:border-blue-400",
      isOpen ? "border-blue-500 ring-2 ring-blue-100" : "",
      disabled
        ? "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
        : "text-gray-900 cursor-text",
      className,
    );
  };

  const getDropdownClasses = () => {
    return cn(
      "absolute top-full left-0 right-0 z-[100] mt-1",
      "bg-white border border-gray-200 rounded-lg shadow-lg",
      "max-h-[300px] flex flex-col",
    );
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    if (onSearchTermChange) {
      onSearchTermChange(term);
    }
  };

  const handleSearchClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const getDisplayText = (selectedValues: (string | number)[]) => {
    if (!selectedValues || selectedValues.length === 0) {
      return placeholder;
    }

    const selectedOptions = options.filter((option) =>
      selectedValues.includes(option.value),
    );

    if (selectedOptions.length <= maxDisplayItems) {
      return selectedOptions.map((option) => option.label).join(", ");
    }

    return `${selectedOptions
      .slice(0, maxDisplayItems)
      .map((option) => option.label)
      .join(", ")} +${selectedOptions.length - maxDisplayItems} more`;
  };

  const toggleOption = (
    optionValue: string | number,
    currentValues: (string | number)[],
    onChange: (values: (string | number)[]) => void,
  ) => {
    let newValues;

    const isSelected = currentValues.includes(optionValue);

    if (isSelected) {
      newValues = currentValues.filter((value) => value !== optionValue);
    } else {
      if (maxSelection && maxSelection === 1) {
        newValues = [optionValue];
      } else if (maxSelection && currentValues.length >= maxSelection) {
        newValues = [...currentValues];
      } else {
        newValues = [...currentValues, optionValue];
      }
    }
    if (onChange) {
      onChange(newValues);
    }

    setSearchTerm("");
    if (onSearchTermChange) {
      onSearchTermChange("");
    }

    if (closeOnSelect) {
      setIsOpen(false);
    }
  };

  const clearAll = (onChange: (values: (string | number)[]) => void) => {
    onChange([]);
    if (onChange) {
      onChange([]);
    }
  };

  const handleDropdownToggle = () => {
    if (!disabled) setIsOpen(!isOpen);
  };

  const handleClearClick = (
    e: React.MouseEvent,
    onChange: (values: (string | number)[]) => void,
  ) => {
    e.stopPropagation();
    clearAll(onChange);
  };

  const filteredOptions = getFilteredOptions();

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={[]}
      render={({ field: { value = [], onChange } }) => (
        <div ref={dropdownRef} className="relative w-full">
          <div
            className={getBaseClasses(value.length > 0)}
            onClick={() => {
              if (!disabled) {
                setIsOpen(true);
              }
            }}
          >
            {value.slice(0, maxDisplayItems).map((val: string | number) => {
              const opt = options.find((o) => o.value === val);
              if (!opt) return null;
              return (
                <span
                  key={val}
                  className="flex items-center gap-1 bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs"
                  onClick={(e) => e.stopPropagation()}
                >
                  {opt.label}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleOption(val, value, onChange);
                      }}
                      className="hover:text-red-500 hover:bg-gray-200 rounded-full p-0.5"
                    >
                      <X size={12} />
                    </button>
                  )}
                </span>
              );
            })}

            {value.length > maxDisplayItems && (
              <TooltipProvider>
                <RadixTooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <span className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs font-medium cursor-help">
                      +{value.length - maxDisplayItems} more
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-sm" side="top">
                    {value
                      .slice(maxDisplayItems)
                      .map(
                        (v: string | number) =>
                          options.find((o) => o.value === v)?.label,
                      )
                      .filter(Boolean)
                      .join(", ")}
                  </TooltipContent>
                </RadixTooltip>
              </TooltipProvider>
            )}

            {searchable ? (
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  handleSearchChange(e);
                  if (!disabled && !isOpen) setIsOpen(true);
                }}
                onClick={(e) => {
                  handleSearchClick(e);
                  if (!disabled && !isOpen) setIsOpen(true);
                }}
                onFocus={() => {
                  // Removed auto-open on focus
                }}
                placeholder={value.length === 0 ? placeholder : ""}
                className="flex-1 bg-transparent min-w-[50px] outline-none text-sm text-gray-900 py-1 px-1"
                disabled={disabled}
              />
            ) : (
              <span
                className={cn(
                  "flex-1 px-1 truncate",
                  !value || value.length === 0
                    ? "text-gray-400"
                    : "text-gray-900",
                )}
              >
                {value.length === 0 ? placeholder : ""}
              </span>
            )}

            <div className="absolute right-0 top-0 h-full flex items-center pr-3 gap-1">
              {clearable && value && value.length > 0 && !disabled && (
                <button
                  type="button"
                  onClick={(e) => handleClearClick(e, onChange)}
                  className="p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
                  title="Clear all"
                >
                  <X size={14} />
                </button>
              )}

              <div
                className={cn(
                  "transition-transform duration-200 opacity-50 cursor-pointer",
                  isOpen ? "rotate-180" : "",
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDropdownToggle();
                }}
              >
                <ChevronDown size={18} />
              </div>
            </div>
          </div>

          {isOpen && !disabled && (
            <div className={getDropdownClasses()}>
              <div className="py-1 overflow-y-auto max-h-[240px] p-1">
                {(() => {
                  if ((maxSelection && maxSelection <= 1) || filteredOptions.length === 0) return null;
                  const safeValue = Array.isArray(value) ? value : (value ? [value] : []);
                  const selectableOptions = filteredOptions.filter(o => !o.disabled);
                  if (selectableOptions.length === 0) return null;
                  const isAllSelected = selectableOptions.every(o => safeValue.includes(o.value));
                  
                  return (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        let newValues;
                        if (isAllSelected) {
                          newValues = safeValue.filter(v => !selectableOptions.find(o => o.value === v));
                        } else {
                          newValues = Array.from(new Set([...safeValue, ...selectableOptions.map(o => o.value)]));
                        }
                        onChange(newValues);
                      }}
                      className="flex items-center gap-3 px-3 py-2 text-sm rounded-md cursor-pointer hover:bg-gray-100 text-gray-700 font-medium transition-colors border-b border-gray-100 mb-1"
                    >
                      <div
                        className={cn(
                          "w-4 h-4 rounded border flex items-center justify-center transition-colors",
                          isAllSelected
                            ? "bg-blue-600 border-blue-600"
                            : "border-gray-300 bg-white"
                        )}
                      >
                        {isAllSelected && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      {isAllSelected ? "Deselect All" : "Select All"}
                    </div>
                  );
                })()}
                {filteredOptions.length === 0 ? (
                  <div className="px-3 py-8 text-center text-sm text-gray-500 font-medium">
                    {searchTerm ? "No options found" : "No options available"}
                  </div>
                ) : (
                  filteredOptions.map((option, idx) => {
                    const isSelected = value.includes(option.value);
                    const isDisabled = option.disabled;

                    return (
                      <div
                        key={String(idx)}
                        onClick={() =>
                          !isDisabled &&
                          toggleOption(option.value, value, onChange)
                        }
                        className={cn(
                          "flex items-center gap-3 px-3 py-2 my-0.5 text-sm rounded-md cursor-pointer transition-all duration-200",
                          isSelected
                            ? "bg-blue-50 text-blue-700 font-medium"
                            : "text-gray-700 hover:bg-gray-50",
                          isDisabled ? "opacity-40 cursor-not-allowed" : "",
                        )}
                        title={option.description}
                      >
                        <div
                          className={cn(
                            "w-4 h-4 rounded border flex items-center justify-center transition-colors",
                            isSelected
                              ? "bg-blue-600 border-blue-600"
                              : "border-gray-300 bg-white",
                          )}
                        >
                          {isSelected && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="truncate">{option.label}</div>
                          {option.description && (
                            <div className="text-[11px] text-gray-500 truncate mt-0.5">
                              {option.description}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {value && value.length > maxDisplayItems && (
                <div className="px-4 py-2 text-[11px] text-gray-500 border-t border-gray-100 bg-gray-50/50">
                  {value.length} items selected
                </div>
              )}
            </div>
          )}
        </div>
      )}
    />
  );
};

// FormArray Component
// interface FormArrayProps {
//   name: string;
//   form: UseFormReturn<any>;
//   itemFields: FormField[];
//   label?: string;
//   helperText?: string;
//   minItems?: number;
//   maxItems?: number;
//   className?: string;
//   showAddButton?: boolean;
//   showRemoveButton?: boolean;
//   showReorderButtons?: boolean;
//   addButtonText?: string;
//   emptyMessage?: string;
// }

// const FormArrayComponent = ({
//   name,
//   form,
//   itemFields,
//   label,
//   helperText,
//   minItems = 1,
//   maxItems,
//   className = "",
//   showAddButton = true,
//   showRemoveButton = true,
//   showReorderButtons = false,
//   addButtonText = "Add Item",
//   emptyMessage = "No items added yet",
// }: FormArrayProps) => {
//   const { fields, append, remove, move } = useFieldArray({
//     control: form.control,
//     name,
//   });

//   // Create default item based on itemFields
//   const createDefaultItem = () => {
//     const defaultItem: any = {};
//     itemFields.forEach((field) => {
//       switch (field.type) {
//         case "number":
//         case "decimal":
//           defaultItem[field.name] = field.min !== undefined ? field.min : 0;
//           break;
//         case "checkbox":
//           defaultItem[field.name] = false;
//           break;
//         case "multi-select":
//         case "form-array":
//           defaultItem[field.name] = [];
//           break;
//         case "select":
//           defaultItem[field.name] = "";
//           break;
//         default:
//           defaultItem[field.name] =
//             field.defaultValue !== undefined ? field.defaultValue : "";
//       }
//     });
//     return defaultItem;
//   };

//   // Handle add item
//   const handleAddItem = () => {
//     if (!maxItems || fields.length < maxItems) {
//       const newItem = createDefaultItem();
//       append(newItem);

//       // Trigger form state update by setting a value
//       setTimeout(() => {
//         form.trigger(); // This will update the form state properly
//       }, 10);
//     }
//   };

//   // Handle remove item
//   const handleRemoveItem = (index: number) => {
//     if (fields.length > minItems) {
//       remove(index);
//     }
//   };

//   // Handle move item
//   const handleMoveItem = (from: number, to: number) => {
//     move(from, to);
//   };

//   // Get grid classes based on visible fields count
//   const getGridClasses = (visibleFieldsCount: number) => {
//     if (visibleFieldsCount <= 3) {
//       return `grid grid-cols-${visibleFieldsCount} gap-4`;
//     } else if (visibleFieldsCount <= 6) {
//       return `grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${Math.min(
//         visibleFieldsCount,
//         3
//       )} gap-4`;
//     } else {
//       return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4";
//     }
//   };

//   // Render individual field with optimized change handling
//   const renderField = (field: FormField, itemIndex: number) => {
//     const fieldName = `${name}.${itemIndex}.${field.name}`;
//     const error = FormService.getFieldError(form, fieldName);

//     return (
//       <div key={field.name} className="space-y-1">
//         {field.label && (
//           <label
//             htmlFor={fieldName}
//             className="block text-sm font-medium text-gray-700"
//           >
//             {field.label}
//             {field.required && <span className="text-red-500 ml-1">*</span>}
//           </label>
//         )}

//         <Controller
//           name={fieldName}
//           control={form.control}
//           defaultValue={field.defaultValue || ""}
//           render={({ field: controllerField }) => {
//             const commonProps = {
//               ...controllerField,
//               id: fieldName,
//               className: `w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
//                 error ? "border-red-500" : ""
//               }`,
//               placeholder: field.placeholder,
//               disabled: field.disabled,
//               readOnly: field.readonly,
//               // Optimize onChange to prevent excessive re-renders
//               onChange: (e: any) => {
//                 const value = e.target ? e.target.value : e;
//                 controllerField.onChange(value);
//               },
//             };

//             switch (field.type) {
//               case "text":
//               case "email":
//               case "password":
//               case "phone":
//               case "url":
//                 return (
//                   <div className="relative">
//                     {field.prefix && (
//                       <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
//                         {field.prefix}
//                       </span>
//                     )}
//                     <input
//                       {...commonProps}
//                       type={field.type}
//                       className={
//                         field.prefix
//                           ? `${commonProps.className} pl-8`
//                           : commonProps.className
//                       }
//                       minLength={field.minLength}
//                       maxLength={field.maxLength}
//                     />
//                     {field.suffix && (
//                       <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
//                         {field.suffix}
//                       </span>
//                     )}
//                   </div>
//                 );

//               case "number":
//               case "decimal":
//               case "negative-decimal":
//                 return (
//                   <div className="relative">
//                     {field.prefix && (
//                       <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
//                         {field.prefix}
//                       </span>
//                     )}
//                     <input
//                       {...commonProps}
//                       type="number"
//                       className={
//                         field.prefix
//                           ? `${commonProps.className} pl-8`
//                           : commonProps.className
//                       }
//                       min={field.min}
//                       max={field.max}
//                     />
//                   </div>
//                 );

//               case "datepicker":
//                 return <input {...commonProps} type="date" />;

//               case "time":
//                 return <input {...commonProps} type="time" />;

//               case "select":
//                 return (
//                   <select {...commonProps}>
//                     <option value="">
//                       {field.placeholder ||
//                         `-- Select ${field.label || field.name} --`}
//                     </option>
//                     {field.options?.map((option) => (
//                       <option
//                         key={String(option.value)}
//                         value={String(option.value)}
//                         disabled={option.disabled}
//                       >
//                         {option.label}
//                       </option>
//                     ))}
//                   </select>
//                 );
//               case "multi-select":
//                 return (
//                   <MultiSelectDropdown
//                     name={fieldName}
//                     control={form.control}
//                     options={field.options || []}
//                     placeholder={
//                       field.placeholder ||
//                       `Select ${field.label || field.name}...`
//                     }
//                     disabled={field.disabled}
//                     searchable={field.searchable !== false}
//                     clearable={field.clearable !== false}
//                     maxDisplayItems={field.maxDisplayItems || 3}
//                     closeOnSelect={field.closeOnSelect || false}
//                     maxSelection={field.maxSelection}
//                     onChange={(values) => {
//                       controllerField.onChange(values);
//                     }}
//                   />
//                 );

//               case "textarea":
//                 return (
//                   <textarea
//                     {...commonProps}
//                     rows={field.rows || 3}
//                     cols={field.cols}
//                   />
//                 );
//               case "checkbox":
//                 return (
//                   <div className="flex items-center">
//                     <input
//                       {...controllerField}
//                       type="checkbox"
//                       id={fieldName}
//                       className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
//                       checked={controllerField.value || false}
//                       onChange={(e) =>
//                         controllerField.onChange(e.target.checked)
//                       }
//                     />
//                     <label
//                       htmlFor={fieldName}
//                       className="ml-2 text-sm text-gray-700"
//                     >
//                       {field.label}
//                     </label>
//                   </div>
//                 );

//               default:
//                 return <input {...commonProps} type="text" />;
//             }
//           }}
//         />

//         {error && <span className="text-red-500 text-sm">{error}</span>}

//         {field.helperText && !error && (
//           <span className="text-gray-500 text-sm">{field.helperText}</span>
//         )}
//       </div>
//     );
//   };

//   // Check if we can add more items
//   const canAddItem = !maxItems || fields.length < maxItems;
//   const canRemoveItem = fields.length > minItems;

//   const getVisibleItemFields = (itemIndex: number) => {
//     // Only get current values for this specific item to reduce watch subscriptions
//     const itemPath = `${name}.${itemIndex}`;
//     const currentItemValues = form.getValues(itemPath) || {};

//     return itemFields.filter((field) => {
//       if (!field.showWhen) return true;

//       const fieldPath = `${name}.${itemIndex}.${field.name}`;
//       // Pass only the current item values instead of entire form
//       const shouldShow = field.showWhen(currentItemValues, field.name);
//       return shouldShow;
//     });
//   };

//   return (
//     <div className={`space-y-4 ${className}`}>
//       {/* Header */}
//       {label && (
//         <div className="flex items-center justify-between">
//           <h3 className="text-lg font-medium text-gray-900">{label}</h3>
//           {showAddButton && canAddItem && (
//             <button
//               type="button"
//               onClick={handleAddItem}
//               className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:ring-2 focus:ring-blue-500 transition-colors"
//             >
//               <Plus className="w-4 h-4" />
//               {addButtonText}
//             </button>
//           )}
//         </div>
//       )}

//       {helperText && <p className="text-sm text-gray-600">{helperText}</p>}

//       {/* Items */}
//       {fields.length === 0 ? (
//         <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
//           <p>{emptyMessage}</p>
//           {showAddButton && canAddItem && (
//             <button
//               type="button"
//               onClick={handleAddItem}
//               className="mt-2 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
//             >
//               <Plus className="w-4 h-4" />
//               {addButtonText}
//             </button>
//           )}
//         </div>
//       ) : (
//         <div className="space-y-4">
//           {fields.map((field, index) => {
//             const visibleFields = getVisibleItemFields(index);
//             const gridClasses = getGridClasses(visibleFields.length);

//             if (visibleFields.length === 0) {
//               return (
//                 <div
//                   key={field.id}
//                   className="p-4 border border-yellow-200 rounded-lg bg-yellow-50"
//                 >
//                   <div className="flex items-center justify-between">
//                     <p className="text-yellow-800">
//                       No fields visible for this item. Please check field
//                       conditions.
//                     </p>
//                     {showRemoveButton && canRemoveItem && (
//                       <button
//                         type="button"
//                         onClick={() => handleRemoveItem(index)}
//                         className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
//                         title="Remove item"
//                       >
//                         <Trash2 className="w-4 h-4" />
//                       </button>
//                     )}
//                   </div>
//                 </div>
//               );
//             }

//             return (
//               <div
//                 key={field.id}
//                 className="p-4 border rounded-lg transition-colors"
//               >
//                 <div className="flex items-start gap-4">
//                   {/* Reorder handle */}
//                   {showReorderButtons && fields.length > 1 && (
//                     <div className="flex flex-col gap-1 mt-8">
//                       <button
//                         type="button"
//                         onClick={() =>
//                           index > 0 && handleMoveItem(index, index - 1)
//                         }
//                         disabled={index === 0}
//                         className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
//                         title="Move up"
//                       >
//                         <GripVertical className="w-4 h-4" />
//                       </button>
//                     </div>
//                   )}

//                   {/* Fields - Only render visible fields */}
//                   <div className={`flex-1 ${gridClasses}`}>
//                     {visibleFields.map((itemField) =>
//                       renderField(itemField, index)
//                     )}
//                   </div>

//                   {/* Remove button */}
//                   {showRemoveButton && canRemoveItem && (
//                     <div className="mt-8">
//                       <button
//                         type="button"
//                         onClick={() => handleRemoveItem(index)}
//                         className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
//                         title="Remove item"
//                       >
//                         <Trash2 className="w-4 h-4" />
//                       </button>
//                     </div>
//                   )}
//                 </div>

//                 {/* Item number */}
//                 <div className="flex justify-between items-center mt-2 pt-2 border-gray-200">
//                   {maxItems && (
//                     <span className="text-xs text-gray-500">
//                       {fields.length} of {maxItems} items
//                     </span>
//                   )}
//                 </div>
//               </div>
//             );
//           })}
//         </div>
//       )}

//       {/* Add button at bottom */}
//       {fields.length > 0 && showAddButton && canAddItem && (
//         <button
//           type="button"
//           onClick={handleAddItem}
//           className="w-full py-3 text-sm font-medium text-blue-600 bg-blue-50 border-2 border-dashed border-blue-200 rounded-md hover:bg-blue-100 focus:ring-2 focus:ring-blue-500 transition-colors"
//         >
//           <Plus className="w-4 h-4 inline mr-2" />
//           {addButtonText}
//         </button>
//       )}
//     </div>
//   );
// };
// Replace the FormArrayComponent in your DynamicForm.tsx with this updated version:

interface FormArrayProps {
  name: string;
  form: UseFormReturn<any>;
  itemFields: FormField[] | ((index: number, formValues?: any) => FormField[]); // UPDATED TYPE
  label?: string;
  helperText?: string;
  minItems?: number;
  maxItems?: number;
  className?: string;
  showAddButton?: boolean;
  showRemoveButton?: boolean;
  showEmptyAddButton?: boolean;
  showReorderButtons?: boolean;
  addButtonText?: string;
  emptyMessage?: string;
}

const FormArrayComponent = ({
  name,
  form,
  itemFields,
  label,
  helperText,
  minItems = 1,
  maxItems,
  className = "",
  showAddButton = true,
  showEmptyAddButton = true,
  showRemoveButton = true,
  showReorderButtons = false,
  addButtonText = "Add Item",
  emptyMessage = "No items added yet",
}: FormArrayProps) => {
  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name,
  });

  // Get itemFields for a specific index - CRITICAL CHANGE
  const getItemFieldsForIndex = (index: number): FormField[] => {
    if (typeof itemFields === "function") {
      // Get current form values to pass to the function
      const formValues = form.getValues();
      return itemFields(index, formValues);
    }
    return itemFields;
  };

  // Create default item based on itemFields
  const createDefaultItem = () => {
    const defaultItem: any = {};
    // Use index 0 for default values
    const fieldsToUse = getItemFieldsForIndex(0);

    fieldsToUse.forEach((field) => {
      switch (field.type) {
        case "number":
        case "decimal":
        case "stepper":
        case "number-stepper":
        case "quantity-stepper":
          defaultItem[field.name] = field.min !== undefined ? field.min : 0;
          break;
        case "checkbox":
          defaultItem[field.name] = false;
          break;
        case "multi-select":
        case "form-array":
          defaultItem[field.name] = [];
          break;
        case "select":
          defaultItem[field.name] = "";
          break;
        default:
          defaultItem[field.name] =
            field.defaultValue !== undefined ? field.defaultValue : "";
      }
    });
    return defaultItem;
  };

  // Handle add item
  const handleAddItem = () => {
    if (!maxItems || fields.length < maxItems) {
      const newItem = createDefaultItem();
      append(newItem);
    }
  };

  // Handle remove item
  const handleRemoveItem = (index: number) => {
    if (fields.length > minItems) {
      remove(index);
    }
  };

  // Handle move item
  const handleMoveItem = (from: number, to: number) => {
    move(from, to);
  };

  // Get grid classes based on visible fields count
  const getGridClasses = (visibleFieldsCount: number) => {
    if (visibleFieldsCount <= 3) {
      return `grid grid-cols-${visibleFieldsCount} gap-4`;
    } else if (visibleFieldsCount <= 6) {
      return `grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${Math.min(
        visibleFieldsCount,
        3,
      )} gap-4`;
    } else {
      return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4";
    }
  };

  // Render individual field with optimized change handling
  const renderField = (field: FormField, itemIndex: number) => {
    const fieldName = `${name}.${itemIndex}.${field.name}`;
    const error = FormService.getFieldError(form, fieldName);

    return (
      <div key={field.name} className="space-y-1">
        {field.label && (
          <label
            htmlFor={fieldName}
            className="block text-sm font-medium text-gray-700"
          >
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        <Controller
          name={fieldName}
          control={form.control}
          defaultValue={field.defaultValue || ""}
          render={({ field: controllerField }) => {
            const isFieldDisabled =
              typeof field.disabled === "function"
                ? field.disabled(form.getValues() || {})
                : !!field.disabled;
            const commonProps = {
              ...controllerField,
              id: fieldName,
              className: `w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                error ? "border-red-500" : ""
              }`,
              placeholder: field.placeholder,
              disabled: isFieldDisabled,
              readOnly:
                typeof field.readonly === "function"
                  ? field.readonly(form.getValues() || {})
                  : !!field.readonly,
              onChange: (e: any) => {
                const value = e.target ? e.target.value : e;
                controllerField.onChange(value);
              },
            };

            switch (field.type) {
              case "text":
              case "email":
              case "password":
              case "phone":
              case "url":
                return (
                  <div className="relative">
                    {field.prefix && (
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                        {field.prefix}
                      </span>
                    )}
                    <input
                      {...commonProps}
                      type={field.type}
                      className={
                        field.prefix
                          ? `${commonProps.className} pl-8`
                          : commonProps.className
                      }
                      minLength={field.minLength}
                      maxLength={field.maxLength}
                    />
                    {field.suffix && (
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                        {field.suffix}
                      </span>
                    )}
                  </div>
                );

              case "number":
              case "decimal":
              case "negative-decimal":
              case "stepper":
              case "number-stepper":
              case "quantity-stepper":
                return (
                  <div className="relative">
                    {field.prefix && (
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                        {field.prefix}
                      </span>
                    )}
                    <input
                      {...commonProps}
                      type="number"
                      className={
                        field.prefix
                          ? `${commonProps.className} pl-8`
                          : commonProps.className
                      }
                      min={field.min}
                      max={field.max}
                    />
                  </div>
                );

              case "datepicker":
                return <input {...commonProps} type="date" />;

              case "time":
                return <input {...commonProps} type="time" />;

              case "select":
                return (
                  <select {...commonProps}>
                    <option value="">
                      {field.placeholder ||
                        `Select ${field.label || field.name}`}
                    </option>
                    {field.options?.map((option) => (
                      <option
                        key={String(option.value)}
                        value={String(option.value)}
                        disabled={option.disabled}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                );
              case "multi-select":
                return (
                  <MultiSelectDropdown
                    name={fieldName}
                    control={form.control}
                    options={field.options || []}
                    placeholder={
                      field.placeholder ||
                      `Select ${field.label || field.name}...`
                    }
                    disabled={isFieldDisabled}
                    searchable={field.searchable !== false}
                    clearable={field.clearable !== false}
                    maxDisplayItems={field.maxDisplayItems || 3}
                    closeOnSelect={field.closeOnSelect || false}
                    maxSelection={field.maxSelection}
                    onChange={(values) => {
                      controllerField.onChange(values);
                    }}
                  />
                );

              case "textarea":
                return (
                  <textarea
                    {...commonProps}
                    rows={field.rows || 3}
                    cols={field.cols}
                  />
                );
              case "checkbox":
                return (
                  <div className="flex items-center">
                    <input
                      {...controllerField}
                      type="checkbox"
                      id={fieldName}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      checked={controllerField.value || false}
                      onChange={(e) =>
                        controllerField.onChange(e.target.checked)
                      }
                    />
                    <label
                      htmlFor={fieldName}
                      className="ml-2 text-sm text-gray-700"
                    >
                      {field.label}
                    </label>
                  </div>
                );

              case "switch":
                return (
                  <div className="flex items-center pt-2">
                    <Switch
                      id={fieldName}
                      checked={controllerField.value || false}
                      onCheckedChange={(checked) => {
                        controllerField.onChange(checked);
                      }}
                      disabled={isFieldDisabled}
                    />
                  </div>
                );

              default:
                return <input {...commonProps} type="text" />;
            }
          }}
        />

        {error && <span className="text-red-500 text-sm">{error}</span>}

        {field.helperText && !error && (
          <span className="text-gray-500 text-sm">{field.helperText}</span>
        )}
      </div>
    );
  };

  const canAddItem = !maxItems || fields.length < maxItems;
  const canRemoveItem = fields.length > minItems;

  const getVisibleItemFields = (itemIndex: number) => {
    const itemPath = `${name}.${itemIndex}`;
    const currentItemValues = form.getValues(itemPath) || {};

    // Get fields for this specific index
    const fieldsForIndex = getItemFieldsForIndex(itemIndex);

    return fieldsForIndex.filter((field) => {
      if (!field.showWhen) return true;
      const shouldShow = field.showWhen(currentItemValues, field.name);
      return shouldShow;
    });
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header */}
      {label && (
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">{label}</h3>
          {showAddButton && !showEmptyAddButton && canAddItem && (
            <button
              type="button"
              onClick={handleAddItem}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              <Plus className="w-4 h-4" />
              {addButtonText}
            </button>
          )}
        </div>
      )}

      {helperText && <p className="text-sm text-gray-600">{helperText}</p>}

      {/* Items */}
      {fields.length === 0 ? (
        <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
          <p>{emptyMessage}</p>
          {showEmptyAddButton && canAddItem && (
            <button
              type="button"
              onClick={handleAddItem}
              className="mt-2 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
            >
              <Plus className="w-4 h-4" />
              {addButtonText}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {fields.map((field, index) => {
            const visibleFields = getVisibleItemFields(index);
            const gridClasses = getGridClasses(visibleFields.length);

            if (visibleFields.length === 0) {
              return (
                <div
                  key={field.id}
                  className="p-4 border border-yellow-200 rounded-lg bg-yellow-50"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-yellow-800">
                      No fields visible for this item. Please check field
                      conditions.
                    </p>
                    {showRemoveButton && canRemoveItem && (
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                        title="Remove item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            }

            return (
              <div
                key={field.id}
                className="p-4 border rounded-lg transition-colors"
              >
                <div className="flex items-start gap-4">
                  {/* Reorder handle */}
                  {showReorderButtons && fields.length > 1 && (
                    <div className="flex flex-col gap-1 mt-8">
                      <button
                        type="button"
                        onClick={() =>
                          index > 0 && handleMoveItem(index, index - 1)
                        }
                        disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                        title="Move up"
                      >
                        <GripVertical className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Fields - Only render visible fields */}
                  <div className={`flex-1 ${gridClasses}`}>
                    {visibleFields.map((itemField) =>
                      renderField(itemField, index),
                    )}
                  </div>

                  {/* Remove button */}
                  {showRemoveButton && canRemoveItem && (
                    <div className="mt-8">
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                        title="Remove item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Item number */}
                <div className="flex justify-between items-center mt-2 pt-2 border-gray-200">
                  {maxItems && (
                    <span className="text-xs text-gray-500">
                      {fields.length} of {maxItems} items
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add button at bottom */}
      {fields.length > 0 && showAddButton && canAddItem && (
        <button
          type="button"
          onClick={handleAddItem}
          className="w-full py-3 text-sm font-medium text-blue-600 bg-blue-50 border-2 border-dashed border-blue-200 rounded-md hover:bg-blue-100 focus:ring-2 focus:ring-blue-500 transition-colors"
        >
          <Plus className="w-4 h-4 inline mr-2" />
          {addButtonText}
        </button>
      )}
    </div>
  );
};
// Field Renderer Component
interface FieldRendererProps {
  field: FormField;
  form: UseFormReturn<any>;
  formClasses: any;
  loading: boolean;
  focusedField: string | null;
  setFocusedField: (field: string | null) => void;
  verificationStatuses: Record<string, FormField["verificationStatus"]>;
  onFieldChange?: (fieldName: string, value: any) => void;
  onVerificationStatusChange?: (
    fieldName: string,
    status: FormField["verificationStatus"],
  ) => void;
}

const FieldRenderer = ({
  field,
  form,
  formClasses,
  loading,
  focusedField,
  setFocusedField,
  verificationStatuses,
  onFieldChange,
  onVerificationStatusChange,
}: FieldRendererProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const error = FormService.getFieldError(form, field.name);
  const currentValues = form.getValues() || {};
  let isFieldDisabled = false;
  try {
    isFieldDisabled =
      typeof field.disabled === "function"
        ? field.disabled(currentValues)
        : !!field.disabled;
  } catch (e) {
    console.error(`Error evaluating disabled state for field ${field.name}`, e);
  }
  const isDisabled = loading || isFieldDisabled;
  const isFocused = focusedField === field.name;

  const currentVerificationStatus =
    verificationStatuses[field.name] ||
    field.verificationStatus ||
    "unverified";
  const currentValue = form.watch(field.name);

  // Get field classes
  const fieldClasses = FormService.getFieldClasses(
    field,
    formClasses,
    !!error,
    isDisabled,
  );

  // Get container classes
  const getContainerClasses = () => {
    return [
      fieldClasses.container,
      field.className,
      field.fullWidth ? "form-field-full-width" : "",
      field.inline ? "form-field-inline" : "",
      isFocused ? formClasses.focused : "",
      error ? formClasses.invalid : formClasses.valid,
    ]
      .filter(Boolean)
      .join(" ");
  };

  const isReadOnlyDefined =
    typeof field.readonly === "function"
      ? field.readonly(currentValues)
      : !!field.readonly;

  const isAutofillDisabled = !!field.disableAutofill;

  // Get common props
  const getCommonProps = () => {
    const props: any = {
      ...form?.register?.(field?.name),
      id: field.name,
      defaultValue: field.defaultValue,
      disabled: isDisabled,
      readOnly: isReadOnlyDefined || (isAutofillDisabled && !isUnlocked),
      autoComplete: field.autoComplete || (isAutofillDisabled ? "off" : undefined),
      autoFocus: field.autoFocus,
      tabIndex: field.tabIndex,
      style: { width: field.width, ...field.style },
    };

    if (isAutofillDisabled) {
      props.autoCorrect = "off";
      props.autoCapitalize = "off";
      props.spellCheck = false;
      props["data-lpignore"] = "true";
      props["data-1p-ignore"] = "true";
      props["data-form-type"] = "other";
    }

    return props;
  };

  // Event handlers
  const handleFocus = (e: React.FocusEvent<any>) => {
    setIsUnlocked(true);
    setFocusedField(field.name);
  };
  const handleBlur = () => setFocusedField(null);

  const handleFieldChange = (value: any) => {
    const currentValue = form.getValues(field.name);
    if (currentValue !== value) {
      form.setValue(field.name, value);
    }
    if (onFieldChange) {
      onFieldChange(field.name, value);
    }
  };

  const handleVerify = async () => {
    const currentValue = form.getValues(field.name);
    await FormService.handleFieldVerification(
      field,
      currentValue,
      onVerificationStatusChange,
    );
  };

  const renderVerifyButton = () => {
    if (!field.showVerifyButton) return null;

    const isVerifyDisabled = FormService.isVerifyButtonDisabled(
      { ...field, verificationStatus: currentVerificationStatus },
      currentValue,
    );

    const buttonText = FormService.getVerifyButtonText({
      ...field,
      verificationStatus: currentVerificationStatus,
    });

    const getButtonClasses = () => {
      let baseClasses =
        "h-10 px-3 py-2 text-sm transition-all duration-200 flex items-center gap-1 rounded ml-1";
      if (isVerifyDisabled) {
        baseClasses += " bg-blue-400 text-white cursor-not-allowed";
      } else {
        switch (currentVerificationStatus) {
          case "verified":
            baseClasses +=
              " bg-green-100 text-green-700 border border-green-200";
            break;
          case "failed":
            baseClasses +=
              " bg-red-100 text-red-700 border border-red-200 hover:bg-red-200";
            break;
          case "verifying":
            baseClasses += " bg-blue-100 text-blue-700 border border-blue-200";
            break;
          default:
            baseClasses += " bg-blue-600 text-white hover:bg-blue-700";
        }
      }
      return baseClasses;
    };

    const renderButtonIcon = () => {
      switch (currentVerificationStatus) {
        case "verifying":
          return <Loader2 className="w-3 h-3 animate-spin" />;
        case "verified":
          return <Check className="w-3 h-3" />;
        case "failed":
          return <X className="w-3 h-3" />;
        default:
          return null;
      }
    };

    return (
      <button
        type="button"
        onClick={handleVerify}
        disabled={isVerifyDisabled}
        className={getButtonClasses()}
      >
        {renderButtonIcon()}
        {buttonText}
      </button>
    );
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ script: "sub" }, { script: "super" }],
      [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      ["blockquote", "code-block"],
      ["link", "image", "video"],
      ["clean"],
    ],
    history: {
      delay: 500,
      maxStack: 100,
      userOnly: true,
    },
  };

  const renderLabel = () => {
    if (!field.label && field.type !== "checkbox" && field.type !== "radio")
      return null;

    if (
      (field.type === "rich-text" || field.type === "html-text") &&
      field.placeholderNeeded &&
      field.placeholders &&
      field.placeholders.length > 0
    ) {
      return (
        <div className="flex items-center justify-between mb-2">
          {/* Label on left */}
          <label
            htmlFor={field.name}
            className={fieldClasses.label + "text-sm font-medium text-gray-700"}
          >
            {field.icon && (
              <span className="form-field-icon">{field.icon}</span>
            )}
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>

          {/* Placeholder button on right */}
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              const event = new CustomEvent("openPlaceholderModal", {
                detail: { fieldName: field.name },
              });
              document.dispatchEvent(event);
            }}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:border-blue-300 active:bg-blue-200 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            title="Click to open placeholder selector"
            aria-label="Insert placeholder"
          >
            <span className="text-base leading-none">+</span>
            Insert Placeholder
          </Button>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2 mb-1.5">
        <label htmlFor={field.name} className={fieldClasses.label}>
          {field.icon && <span className="form-field-icon">{field.icon}</span>}
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>

        {field.showHelperTooltip && field.tooltipText && (
          <Tooltip
            content={field.tooltipText}
            position={field.tooltipPosition || "top"}
            className="flex items-center text-black font-medium"
          >
            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
          </Tooltip>
        )}
      </div>
    );
  };

  const renderInput = () => {
    if (field.customRender) {
      return field.customRender(field, form, error);
    }

    const inputProps = {
      ...getCommonProps(),
      onFocus: handleFocus,
      onMouseDown: () => setIsUnlocked(true),
      onBlur: handleBlur,
    };

    switch (field.type) {
      case "autoPopulated":
        return <p {...getCommonProps()}>{field.value}</p>;
      case "hidden":
        return <input {...inputProps} type="hidden" />;

      case "text":
      case "email":
      case "phone":
      case "url":
        return (
          <div className="flex items-center">
            <div className="form-input-wrapper flex-1">
              {field.prefix && (
                <span className="form-input-prefix">{field.prefix}</span>
              )}
              <input
                {...inputProps}
                type={field.type}
                placeholder={field.placeholder}
                className={fieldClasses.input}
                minLength={field.minLength}
                maxLength={field.maxLength}
              />
              {field.suffix && (
                <span className="form-input-suffix">{field.suffix}</span>
              )}
            </div>
            {renderVerifyButton()}
          </div>
        );

      case "password":
        return (
          <div className="flex items-center">
            <div className="form-input-wrapper flex-1 relative">
              {field.prefix && (
                <span className="form-input-prefix">{field.prefix}</span>
              )}
              <input
                {...inputProps}
                type={
                  isAutofillDisabled
                    ? "text"
                    : showPassword
                      ? "text"
                      : "password"
                }
                placeholder={field.placeholder}
                className={cn(fieldClasses.input, "pr-10")}
                style={
                  isAutofillDisabled
                    ? ({
                        WebkitTextSecurity: showPassword ? "none" : "disc",
                        ...field.style,
                      } as any)
                    : field.style
                }
                minLength={field.minLength}
                maxLength={field.maxLength}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
              {field.suffix && (
                <span className="form-input-suffix">{field.suffix}</span>
              )}
            </div>
            {renderVerifyButton()}
          </div>
        );

      case "slider":
        return (
          <Controller
            name={field.name}
            control={form.control}
            defaultValue={field.defaultValue || field.min || 1}
            render={({ field: controllerField }) => {
              const handleSliderChange = (values: number[]) => {
                const value = values[0];
                controllerField.onChange(value);
                if (onFieldChange) {
                  onFieldChange(field.name, value);
                }
                if (field.onChange) {
                  field.onChange(value);
                }
              };

              return (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      Value: {controllerField.value || field.min || 1}
                      {field.suffix && (
                        <span className="text-gray-500 ml-1">
                          {field.suffix}
                        </span>
                      )}
                    </span>
                    {field.max && (
                      <span className="text-sm text-gray-500">
                        Max: {field.max}
                      </span>
                    )}
                  </div>

                  <Slider
                    id={field.name}
                    min={field.min || 1}
                    max={field.max || 5}
                    step={field.step || 1}
                    value={[controllerField.value || field.min || 1]}
                    onValueChange={handleSliderChange}
                    disabled={isDisabled}
                    className="w-full"
                  />

                  {(field.max || 5) - (field.min || 1) <= 10 && (
                    <div className="flex justify-between text-xs text-gray-500 px-1">
                      {Array.from(
                        {
                          length:
                            ((field.max || 5) - (field.min || 1)) /
                              (field.step || 1) +
                            1,
                        },
                        (_, i) => (field.min || 1) + i * (field.step || 1),
                      ).map((tick) => (
                        <span key={tick}>{tick}</span>
                      ))}
                    </div>
                  )}
                </div>
              );
            }}
          />
        );

      case "country-phone":
        return (
          <Controller
            name={field.name}
            control={form.control}
            defaultValue={field.defaultValue || ""}
            render={({ field: controllerField }) => {
              const handlePhoneChange = (value: string) => {
                controllerField.onChange(value);
                if (onFieldChange) {
                  onFieldChange(field.name, value);
                }
                if (field.onChange) {
                  field.onChange(value);
                }
              };

              return (
                <div className="space-y-2">
                  <PhoneInputComponent
                    value={controllerField.value}
                    onChange={handlePhoneChange}
                    placeholder={field.placeholder || "Enter phone number"}
                    className={
                      isDisabled ? "opacity-50 cursor-not-allowed" : ""
                    }
                  />
                </div>
              );
            }}
          />
        );

      case "textarea":
        return (
          <div className="flex items-start">
            <textarea
              {...inputProps}
              placeholder={field.placeholder}
              className={`${fieldClasses.textarea} flex-1`}
              rows={field.rows || 4}
              cols={field.cols}
              minLength={field.minLength}
              maxLength={field.maxLength}
            />
            {renderVerifyButton()}
          </div>
        );

      case "stepper":
      case "number-stepper":
      case "quantity-stepper":
        return (
          <Controller
            name={field.name}
            control={form.control}
            defaultValue={field.defaultValue !== undefined ? field.defaultValue : (field.min || 1)}
            render={({ field: controllerField }) => {
              const currentValue = controllerField.value !== undefined && controllerField.value !== "" && !isNaN(Number(controllerField.value)) ? Number(controllerField.value) : (field.min || 1);
              const minVal = field.min !== undefined ? field.min : 1;
              const maxVal = field.max !== undefined ? field.max : 999;

              const handleValueChange = (newVal: number) => {
                let corrected = newVal;
                if (isNaN(corrected) || corrected < minVal) {
                  corrected = minVal;
                } else if (corrected > maxVal) {
                  corrected = maxVal;
                }
                controllerField.onChange(corrected);
                if (onFieldChange) {
                  onFieldChange(field.name, corrected);
                }
                if (field.onChange) {
                  field.onChange(corrected);
                }
              };

              return (
                <div className="flex items-center">
                  <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden bg-white h-11 shadow-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 flex-1">
                    <button
                      type="button"
                      onClick={() => handleValueChange(Math.max(minVal, currentValue - 1))}
                      disabled={currentValue <= minVal || field.disabled}
                      className="w-11 h-full flex items-center justify-center text-gray-600 bg-gray-50 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed border-r border-gray-200 transition-colors text-lg font-medium select-none"
                      aria-label="Decrement"
                    >
                      −
                    </button>
                    <input
                      id={field.name}
                      type="number"
                      min={minVal}
                      max={maxVal}
                      disabled={field.disabled}
                      value={controllerField.value !== undefined ? controllerField.value : minVal}
                      onChange={(e) => {
                        const valStr = e.target.value;
                        if (valStr === "") {
                          controllerField.onChange("");
                          if (onFieldChange) onFieldChange(field.name, "");
                        } else {
                          const parsed = parseInt(valStr, 10);
                          if (!isNaN(parsed)) {
                            if (parsed < minVal) {
                              handleValueChange(minVal);
                            } else if (parsed > maxVal) {
                              handleValueChange(maxVal);
                            } else {
                              controllerField.onChange(parsed);
                              if (onFieldChange) onFieldChange(field.name, parsed);
                              if (field.onChange) field.onChange(parsed);
                            }
                          }
                        }
                      }}
                      onBlur={(e) => {
                        const valStr = e.target.value;
                        const parsed = parseInt(valStr, 10);
                        if (valStr === "" || isNaN(parsed) || parsed < minVal) {
                          handleValueChange(minVal);
                        } else if (parsed > maxVal) {
                          handleValueChange(maxVal);
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="flex-1 w-full text-center font-medium text-gray-900 focus:outline-none border-0 bg-transparent text-sm appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <button
                      type="button"
                      onClick={() => handleValueChange(Math.min(maxVal, currentValue + 1))}
                      disabled={currentValue >= maxVal || field.disabled}
                      className="w-11 h-full flex items-center justify-center text-gray-600 bg-gray-50 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed border-l border-gray-200 transition-colors text-lg font-medium select-none"
                      aria-label="Increment"
                    >
                      +
                    </button>
                  </div>
                  {renderVerifyButton()}
                </div>
              );
            }}
          />
        );

      case "number":
      case "decimal":
      case "negative-decimal":
        return (
          <div className="flex items-center">
            <input
              {...inputProps}
              type="number"
              placeholder={field.placeholder}
              className={`${fieldClasses.input} flex-1`}
              min={field.allowNegativeValue === false ? 0 : field.min}
              max={field.max}
              step={field.step || (field.type === "decimal" ? 0.01 : 1)}
              onWheel={(e) => e.currentTarget.blur()}
              onKeyDown={(e) => {
                const preventNegative = field.allowNegativeValue === false || (field.min !== undefined && field.min >= 0);
                if (preventNegative && e.key === "-") {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                if (e.target.value !== "" && field.max !== undefined && Number(e.target.value) > field.max) {
                  e.target.value = String(field.max);
                }
                if (inputProps.onChange) {
                  inputProps.onChange(e);
                }
              }}
            />
            {renderVerifyButton()}
          </div>
        );

      case "range":
        return (
          <div className="form-range-wrapper">
            <input
              {...inputProps}
              type="range"
              className={fieldClasses.input}
              min={field.min || 0}
              max={field.max || 100}
              step={field.step || 1}
            />
            <span className="form-range-value">{currentValue}</span>
          </div>
        );

      case "color":
        return (
          <input {...inputProps} type="color" className={fieldClasses.input} />
        );

      case "datepicker":
        // Calculate min/max dates based on allowFuture and allowPast
        const getDateConstraints = () => {
          const today = new Date();
          const todayString = format(today, "yyyy-MM-dd");

          let minDate: string | undefined;
          let maxDate: string | undefined;

          // If past dates are not allowed, set min to today
          if (field.allowPast === false) {
            minDate = todayString;
          }

          // If future dates are not allowed, set max to today
          if (field.allowFuture === false) {
            maxDate = todayString;
          }
          return { minDate, maxDate };
        };

        const { minDate, maxDate } = getDateConstraints();

        return (
          <input
            {...inputProps}
            type="date"
            className={fieldClasses.input}
            min={minDate}
            max={maxDate}
          />
        );

      case "time":
        return (
          <input {...inputProps} type="time" className={fieldClasses.input} />
        );

      case "select":
        return (
          <Controller
            name={field.name}
            control={form.control}
            defaultValue={field.defaultValue || ""}
            render={({ field: controllerField }) => (
              <GenericSelect
                value={controllerField.value}
                isSearchable={field.isSearchable}
                disabled={isDisabled}
                placeholder={
                  field.placeholder || `Select ${field.label || field.name}`
                }
                options={field.options}
                className={fieldClasses.select}
                onChange={(value) => {
                  controllerField.onChange(value);

                  if (onFieldChange) {
                    onFieldChange(field.name, value);
                  }

                  if (value && value !== "") {
                    form.clearErrors(field.name);
                  }
                }}
              />
            )}
          />
        );

      case "multi-select":
        return (
          <MultiSelectDropdown
            name={field.name}
            control={form.control}
            options={(field.options as any) || []}
            placeholder={
              field.placeholder || `Select ${field.label || field.name}...`
            }
            disabled={isDisabled}
            onSearchTermChange={(term) => {
              if (field.onSearchChange) {
                field.onSearchChange(term);
              }
            }}
            maxSelection={field.maxSelection}
            onChange={(values) => handleFieldChange(values)}
            className={fieldClasses.select}
            searchable={field.searchable !== false}
            clearable={field.clearable !== false}
            maxDisplayItems={field.maxDisplayItems || 3}
            closeOnSelect={field.closeOnSelect || false}
          />
        );

      case "radio":
        return (
          <div className="form-radio-group flex gap-3">
            {field.options?.map((option) => (
              <label key={String(option.value)} className="text-sm">
                <input
                  {...form.register(field.name)}
                  type="radio"
                  value={String(option.value)}
                  disabled={isDisabled || option.disabled}
                  className={fieldClasses.radio}
                />
                <span className="form-radio-label pl-1">{option.label}</span>
                {option.description && (
                  <span className="form-radio-description">
                    {option.description}
                  </span>
                )}
              </label>
            ))}
          </div>
        );
      case "radio-card":
        return (
          <Controller
            name={field.name}
            control={form.control}
            defaultValue={field.defaultValue || ""}
            render={({ field: controllerField }) => (
              <div
                className={cn(
                  "grid gap-4 w-full",
                  fieldClasses.radioGroup || "grid-cols-1 md:grid-cols-3",
                )}
              >
                {field.options?.map((option) => (
                  <label
                    key={String(option.value)}
                    className={cn(
                      fieldClasses.radioCard ||
                        "flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all duration-200",
                      controllerField.value === option.value
                        ? fieldClasses.radioCardChecked ||
                            "border-blue-500 bg-blue-50"
                        : "border-gray-200 bg-white hover:border-gray-300",
                      isDisabled ? "opacity-50 cursor-not-allowed" : "",
                      option.className,
                    )}
                  >
                    <input
                      type="radio"
                      name={field.name}
                      value={String(option.value)}
                      checked={controllerField.value === option.value}
                      disabled={isDisabled || option.disabled}
                      onChange={() => {
                        controllerField.onChange(option.value);
                        if (onFieldChange)
                          onFieldChange(field.name, option.value);
                      }}
                      className="mt-1 accent-blue-500"
                    />
                    <div>
                      <p
                        className={cn(
                          "font-medium text-gray-900 text-sm",
                          fieldClasses.label,
                        )}
                      >
                        {option.label}
                      </p>
                      {option.description && (
                        <p
                          className={cn(
                            "text-xs text-gray-500 mt-1",
                            fieldClasses.description,
                          )}
                        >
                          {option.description}
                        </p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}
          />
        );
      case "checkbox":
        return (
          <div className="flex items-center gap-2">
            <label className="form-checkbox-wrapper">
              <input
                {...inputProps}
                type="checkbox"
                className={fieldClasses.checkbox}
              />
              <span className="form-checkbox-label ml-2">
                {field.label}
                {field.required && (
                  <span className={fieldClasses.required}>*</span>
                )}
              </span>
            </label>
            {field.showHelperTooltip && field.tooltipText && (
              <Tooltip
                content={field.tooltipText}
                position={field.tooltipPosition || "top"}
                className="flex items-center text-black font-medium"
              >
                <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
              </Tooltip>
            )}
          </div>
        );

      case "switch":
        return (
          <Controller
            name={field.name}
            control={form.control}
            render={({ field: { value, onChange } }) => {
              const handleSwitchChange = (checked: boolean) => {
                onChange(checked);
                if (onFieldChange) {
                  onFieldChange(field.name, checked);
                }
                if (field.onChange) {
                  field.onChange(checked);
                }
              };

              return (
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                      <span>
                        {field.label}
                        {field.required && (
                          <span className={fieldClasses.required}>*</span>
                        )}
                      </span>
                      {field.showHelperTooltip && field.tooltipText && (
                        <Tooltip
                          content={field.tooltipText}
                          position={field.tooltipPosition || "top"}
                          className="flex items-center text-black font-medium"
                        >
                          <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                        </Tooltip>
                      )}
                    </div>
                    {field.helperText && (
                      <div className="text-sm text-gray-500 mt-1">
                        {field.helperText}
                      </div>
                    )}
                  </div>
                  <Switch
                    id={field.name}
                    checked={value || false}
                    disabled={isDisabled}
                    onCheckedChange={handleSwitchChange}
                  />
                </div>
              );
            }}
          />
        );

      case "file":
        return (
          <input
            {...inputProps}
            type="file"
            accept={field.accept}
            multiple={field.multiple}
            className={fieldClasses.file}
          />
        );
      case "multi-file":
        return (
          <FileUploadComponent
            name={field.name}
            form={form}
            field={field}
            accept={field.accept}
            multiple={field.multiple}
            maxFiles={field.maxFiles || 10}
            maxSize={field.maxSize || 10}
            disabled={isDisabled}
            placeholder={
              field.placeholder || "Drag and drop files here, or browse"
            }
            helperText={field.helperText}
            className={fieldClasses.file}
            onChange={(files) => {
              handleFieldChange(field.multiple ? files : files[0] || null);
            }}
          />
        );
      case "rich-text":
        return (
          <Controller
            name={field.name}
            control={form.control}
            defaultValue=""
            render={({ field: { value, onChange, onBlur, ref } }) => {
              const [isModalOpen, setIsModalOpen] = useState(false);
              const quillRef = React.useRef<ReactQuill>(null);

              // Listen for the custom event from the button in label
              React.useEffect(() => {
                const handleOpenModal = (e: CustomEvent) => {
                  if (e.detail.fieldName === field.name) {
                    setIsModalOpen(true);
                  }
                };

                document.addEventListener(
                  "openPlaceholderModal",
                  handleOpenModal as EventListener,
                );
                return () => {
                  document.removeEventListener(
                    "openPlaceholderModal",
                    handleOpenModal as EventListener,
                  );
                };
              }, []);

              // Function to insert placeholder at cursor position
              const handleInsertPlaceholder = (placeholderText: string) => {
                if (!quillRef.current) return;

                const quill = quillRef.current.getEditor();
                const range = quill.getSelection();

                if (range) {
                  // Insert at cursor position
                  quill.insertText(range.index, placeholderText);
                  quill.setSelection(range.index + placeholderText.length, 0);
                } else {
                  // No selection, append to end
                  const currentLength = quill.getLength();
                  quill.insertText(currentLength - 1, placeholderText);
                }

                // Update form value
                const newValue = quill.root.innerHTML;
                onChange(newValue);

                // Focus back on editor
                quill.focus();
              };

              return (
                <>
                  {/* Rich Text Editor with Visual Feedback */}
                  <div
                    className={`
              border rounded-lg transition-all duration-150 max-h-[300px] overflow-y-auto
              ${
                value?.includes("{") && value?.includes("}")
                  ? "border-blue-400 bg-blue-50/10"
                  : "border-gray-300"
              }
              ${field.className || ""}
            `}
                  >
                    <ReactQuill
                      ref={quillRef}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      placeholder={field.placeholder}
                      theme="snow"
                      modules={modules}
                    />
                  </div>

                  {/* Placeholder Modal */}
                  {field.placeholderNeeded && field.placeholders && (
                    <PlaceholderModal
                      isOpen={isModalOpen}
                      onClose={() => setIsModalOpen(false)}
                      placeholders={field.placeholders}
                      onInsert={(placeholderText) => {
                        handleInsertPlaceholder(placeholderText);
                        setIsModalOpen(false);
                      }}
                      fieldType="textarea-with-placeholder"
                    />
                  )}
                </>
              );
            }}
          />
        );
      case "html-text":
        return (
          <Controller
            name={field.name}
            control={form.control}
            defaultValue=""
            render={({ field: { value, onChange, onBlur, ref } }) => {
              const [isModalOpen, setIsModalOpen] = useState(false);
              const quillRef = React.useRef<ReactQuill>(null);

              // Listen for the custom event from the button in label
              React.useEffect(() => {
                const handleOpenModal = (e: CustomEvent) => {
                  if (e.detail.fieldName === field.name) {
                    setIsModalOpen(true);
                  }
                };

                document.addEventListener(
                  "openPlaceholderModal",
                  handleOpenModal as EventListener,
                );
                return () => {
                  document.removeEventListener(
                    "openPlaceholderModal",
                    handleOpenModal as EventListener,
                  );
                };
              }, []);

              // Function to insert placeholder at cursor position
              const handleInsertPlaceholder = (placeholderText: string) => {
                if (!quillRef.current) return;

                const quill = quillRef.current.getEditor();
                const range = quill.getSelection();

                if (range) {
                  // Insert at cursor position
                  quill.insertText(range.index, placeholderText);
                  quill.setSelection(range.index + placeholderText.length, 0);
                } else {
                  // No selection, append to end
                  const currentLength = quill.getLength();
                  quill.insertText(currentLength - 1, placeholderText);
                }

                // Update form value
                const newValue = quill.root.innerHTML;
                onChange(newValue);

                // Focus back on editor
                quill.focus();
              };

              return (
                <>
                  {/* HTML Text Editor with Visual Feedback */}
                  <div
                    className={`
                border rounded-lg transition-all duration-150
                ${
                  value?.includes("{") && value?.includes("}")
                    ? "border-blue-400 bg-blue-50/10"
                    : "border-gray-300"
                }
              `}
                  >
                    <ReactQuill
                      ref={quillRef}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      placeholder={field.placeholder}
                      theme="snow"
                      modules={{ toolbar: false }}
                    />
                  </div>

                  {/* Placeholder Modal */}
                  {field.placeholderNeeded && field.placeholders && (
                    <PlaceholderModal
                      isOpen={isModalOpen}
                      onClose={() => setIsModalOpen(false)}
                      placeholders={field.placeholders}
                      onInsert={(placeholderText) => {
                        handleInsertPlaceholder(placeholderText);
                        setIsModalOpen(false);
                      }}
                      fieldType="textarea-with-placeholder"
                    />
                  )}
                </>
              );
            }}
          />
        );

      case "tags":
        return (
          <Controller
            control={form.control}
            name={field.name}
            render={({ field: c }) => {
              const [tagInput, setTagInput] = React.useState("");

              const handleAddTag = () => {
                const currentTags = Array.isArray(c.value) ? c.value : [];

                // Enforce character limit
                if (tagInput.length > 25) {
                  return; // optionally show toast or error message
                }

                if (tagInput.trim() && !currentTags.includes(tagInput.trim())) {
                  const newTags = [...currentTags, tagInput.trim()];
                  c.onChange(newTags);
                  if (onFieldChange) {
                    onFieldChange(field.name, newTags);
                  }
                  setTagInput("");
                }
              };

              const handleRemoveTag = (tagToRemove: string) => {
                const currentTags = Array.isArray(c.value) ? c.value : [];
                const newTags = currentTags.filter((t) => t !== tagToRemove);
                c.onChange(newTags);
                if (onFieldChange) {
                  onFieldChange(field.name, newTags);
                }
              };

              const handleKeyPress = (
                e: React.KeyboardEvent<HTMLInputElement>,
              ) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddTag();
                }
              };

              return (
                <div>
                  <div className="flex space-x-2">
                    <input
                      className={`w-full h-11 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all`}
                      placeholder={
                        field.placeholder || "Enter a tag and press Enter"
                      }
                      value={tagInput}
                      maxLength={25} // ✅ prevents typing beyond 25 chars
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                    />
                    <Button
                      type="button"
                      onClick={handleAddTag}
                      variant="outline"
                      disabled={!tagInput.trim() || tagInput.length > 25} // ✅ disable button if invalid
                    >
                      Add
                    </Button>
                  </div>
                  {c?.value?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {c.value.map((tag: string, idx: number) => (
                        <span
                          key={idx}
                          className="flex items-center bg-gray-200 px-2 py-1 rounded-full text-sm"
                        >
                          {tag}
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="ml-1 h-4 w-4 p-0 hover:bg-transparent"
                            onClick={() => handleRemoveTag(tag)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </span>
                      ))}
                    </div>
                  )}
                  {tagInput.length > 25 && (
                    <p className="mt-1 text-xs text-red-500">
                      Tag cannot exceed 25 characters
                    </p>
                  )}
                </div>
              );
            }}
          />
        );

      case "text-with-placeholder":
        return (
          <TextWithPlaceholder
            name={field.name}
            control={form.control}
            label={field.label}
            placeholder={field.placeholder}
            placeholders={field.placeholders}
            required={field.required}
            helperText={field.helperText}
            className={fieldClasses.input}
            minLength={field.minLength}
            maxLength={field.maxLength}
            prefix={field.prefix}
            suffix={field.suffix}
            disabled={isDisabled}
            readonly={field.readonly}
            onFieldChange={onFieldChange}
          />
        );

      case "textarea-with-placeholder":
        return (
          <TextareaWithPlaceholder
            name={field.name}
            control={form.control}
            label={field.label}
            placeholder={field.placeholder}
            placeholders={field.placeholders}
            required={field.required}
            helperText={field.helperText}
            className={fieldClasses.textarea}
            rows={field.rows || 4}
            cols={field.cols}
            minLength={field.minLength}
            maxLength={field.maxLength}
            disabled={isDisabled}
            readonly={field.readonly}
            onFieldChange={onFieldChange}
          />
        );

      case "form-array":
        return (
          <FormArrayComponent
            name={field.name}
            form={form}
            itemFields={field.itemFields || []}
            label={field.label}
            helperText={field.helperText}
            minItems={field.minItems}
            maxItems={field.maxItems}
            className={field.className}
            showAddButton={field.showAddButton}
            showEmptyAddButton={field.showEmptyAddButton}
            showRemoveButton={field.showRemoveButton}
            showReorderButtons={field.showReorderButtons}
            addButtonText={field.addButtonText}
            emptyMessage={field.emptyMessage}
          />
        );
      case "button":
        return (
          <div className="mt-6">
            <Button
              type="button"
              variant={field.buttonVariant || "default"}
              onClick={field.onClick}
              disabled={field.buttonDisabled || isDisabled}
              className={cn(field.buttonFullWidth && "w-full", field.className)}
            >
              {field.buttonIcon && (
                <span className="mr-2">{field.buttonIcon}</span>
              )}
              {field.buttonLabel || field.label || "Button"}
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  const renderError = () => {
    if (!error) return null;
    return (
      <span className={fieldClasses.error} role="alert">
        {error}
      </span>
    );
  };

  const renderHelperText = () => {
    if (
      !field.helperText ||
      field.type === "switch" ||
      field.type === "form-array"
    )
      return null;
    return <span className={fieldClasses.helperText}>{field.helperText}</span>;
  };

  if (field.type === "hidden") {
    return renderInput();
  }

  if (field.type === "button") {
    return (
      <div className={`${getContainerClasses()} w-full`} style={field.style}>
        {renderInput()}
      </div>
    );
  }

  return (
    <div className={`${getContainerClasses()} w-full`} style={field.style}>
      {field.type !== "checkbox" &&
        field.type !== "switch" &&
        field.type !== "form-array" &&
        renderLabel()}
      {renderInput()}
      <div className="space-y-1 mt-1">
        {renderError()}
        {!error && renderHelperText()}{" "}
      </div>
    </div>
  );
};

// Main DynamicForm Component
export const DynamicForm: React.FC<DynamicFormProps> = ({
  fields,
  form,
  onSubmit,
  onReset,
  loading = false,
  config = {},
  onFieldChange,
  className = "",
  style = {},
  renderCustomFooter,
  gridColumns = 1,
  responsiveColumns,
  onVerificationStatusChange,
}) => {
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [verificationStatuses, setVerificationStatuses] = useState<
    Record<string, FormField["verificationStatus"]>
  >({});

  // Single form watch - optimization to prevent multiple subscriptions
  const formValues = form.watch();
  const formErrors = form.formState.errors;

  // Get visible fields
  const getVisibleFields = () => {
    return FormService.getVisibleFields(fields, formValues);
  };

  // Get grouped fields - updated to include groupHelperText
  const getGroupedFields = () => {
    const visibleFields = getVisibleFields();
    const groups: Record<string, [FormField[], string]> = {};

    visibleFields.forEach((field) => {
      const groupName = field.group || "default";
      if (!groups[groupName]) {
        groups[groupName] = [[], field.groupHelperText || ""];
      }
      groups[groupName][0].push(field);
    });

    return groups;
  };

  // Merge default classes with config classes
  const getFormClasses = () => {
    return { ...defaultFormClasses, ...config.classes };
  };

  // Get grid column classes
  const getGridColumnClasses = () => {
    let gridClasses = [];
    if (gridColumns > 1) {
      gridClasses.push(`grid grid-cols-${gridColumns}`);
    }

    if (responsiveColumns) {
      if (responsiveColumns.sm)
        gridClasses.push(`sm:grid-cols-${responsiveColumns.sm}`);
      if (responsiveColumns.md)
        gridClasses.push(`md:grid-cols-${responsiveColumns.md}`);
      if (responsiveColumns.lg)
        gridClasses.push(`lg:grid-cols-${responsiveColumns.lg}`);
      if (responsiveColumns.xl)
        gridClasses.push(`xl:grid-cols-${responsiveColumns.xl}`);
    }

    if (gridColumns > 1 || responsiveColumns) {
      gridClasses.push("gap-4");
    }

    return gridClasses.join(" ");
  };

  // Get layout classes
  const getLayoutClasses = () => {
    const formClasses = getFormClasses();
    const baseClass = formClasses.form;
    const layoutClass = config.layout ? `form-layout-${config.layout}` : "";
    const spacingClass = config.spacing ? `form-spacing-${config.spacing}` : "";
    const columnsClass = config.columns ? `form-columns-${config.columns}` : "";

    return [baseClass, layoutClass, spacingClass, columnsClass, className]
      .filter(Boolean)
      .join(" ");
  };

  // Form watch subscription effect
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name && onFieldChange) {
        onFieldChange(name, value[name]);
      }
    });
    return () => subscription.unsubscribe();
  }, [form, onFieldChange]);

  // Verification status change handler
  const handleVerificationStatusChange = (
    fieldName: string,
    status: FormField["verificationStatus"],
  ) => {
    setVerificationStatuses((prev) => ({
      ...prev,
      [fieldName]: status,
    }));

    if (onVerificationStatusChange) {
      onVerificationStatusChange(fieldName, status);
    }
  };

  // Submit handler
  const handleSubmit = async (data: any) => {
    try {
      const payload = FormService.createPayload
        ? FormService.createPayload(data, fields)
        : data;

      await onSubmit?.(payload);

      if (config.resetOnSubmit) {
        form.reset();
      }
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  // Reset handler
  const handleReset = () => {
    form.reset();
    setVerificationStatuses({});
    if (onReset) {
      onReset();
    }
  };

  // Field renderer
  const renderField = (field: FormField) => (
    <FieldRenderer
      key={field.name}
      field={field}
      form={form}
      formClasses={getFormClasses()}
      loading={loading}
      focusedField={focusedField}
      setFocusedField={setFocusedField}
      verificationStatuses={verificationStatuses}
      onFieldChange={onFieldChange}
      onVerificationStatusChange={handleVerificationStatusChange}
    />
  );

  // Helper function to get the border color class for a group
  const getGroupBorderColor = (groupFields: FormField[]) => {
    const fieldWithColor = groupFields.find((field) => field.groupColor);
    return fieldWithColor?.groupColor || "border-l-transparent";
  };

  // Field group renderer with card styling - updated to use groupHelperText
  const renderFieldGroup = (
    groupName: string,
    groupFields: FormField[],
    groupHelperText: string,
  ) => {
    const borderColorClass = getGroupBorderColor(groupFields);
    const gridColumnClasses = getGridColumnClasses();
    const isBorderNeeded = !groupFields.some(
      (field) => field.groupBorderNeeded === false,
    );
    const isColorNeeded = !groupFields.some(
      (field) => field.groupColorNeeded === false,
    );

    if (groupName === "default") {
      return (
        <div key={groupName} className={gridColumnClasses}>
          {groupFields.map(renderField)}
        </div>
      );
    }

    if (!isBorderNeeded) {
      return (
        <div key={groupName} className="mb-8">
          <h5
            className={`text-lg font-semibold text-gray-900 ${groupHelperText ? "mb-1" : "pb-2 mb-4 border-b border-gray-200"}`}
          >
            {groupName}
          </h5>
          {groupHelperText && (
            <p className="page-subtitle mb-6">{groupHelperText}</p>
          )}
          <div className={`form-group-fields ${gridColumnClasses}`}>
            {groupFields.map(renderField)}
          </div>
        </div>
      );
    }

    const getGroupBgColor = (groupFields: FormField[]) => {
      const field = groupFields.find((f) => f.groupBgColor);
      return field?.groupBgColor || "bg-white";
    };

    const getGroupBorderColorFull = (groupFields: FormField[]) => {
      const field = groupFields.find((f) => f.groupBorderColor);
      return field?.groupBorderColor || "border-gray-200";
    };

    const getGroupIcon = (groupFields: FormField[]) => {
      const field = groupFields.find((f) => f.groupIcon);
      return field?.groupIcon || null;
    };

    if (!isColorNeeded) {
      const bgColor = getGroupBgColor(groupFields);
      const borderCol = getGroupBorderColorFull(groupFields);
      const icon = getGroupIcon(groupFields);
      return (
        <div
          key={groupName}
          className={`${bgColor} border ${borderCol} rounded-lg pl-3 pr-3 pb-2 pt-2 mb-4`}
        >
          <h3 className={`text-lg font-semibold text-gray-900 flex items-center gap-2 ${groupHelperText ? "mb-1" : "mb-4"}`}>
            {icon}
            {groupName}
          </h3>
          {groupHelperText && (
            <p className="page-subtitle mb-4">{groupHelperText}</p>
          )}
          <div className={`form-group-fields ${gridColumnClasses}`}>
            {groupFields.map(renderField)}
          </div>
        </div>
      );
    }

    return (
      <div
        key={groupName}
        className={`bg-white border border-gray-200 rounded-lg p-6 border-l-4 mb-4 ${borderColorClass}`}
      >
        <h3
          className={`text-lg font-semibold text-gray-900 ${groupHelperText ? "mb-1" : "mb-4 pb-2"}`}
        >
          {groupName}
        </h3>
        {groupHelperText && (
          <p className="text-sm text-gray-600 mb-6">{groupHelperText}</p>
        )}
        <div className={`form-group-fields ${gridColumnClasses}`}>
          {groupFields.map(renderField)}
        </div>
      </div>
    );
  };

  // Progress calculation and rendering
  const getProgressData = () => {
    if (!config.showProgress) return null;

    const totalFields = fields.filter((f) => f.type !== "hidden").length;
    const filledFields = fields.filter((f) => {
      const value = formValues[f.name];
      return value !== undefined && value !== null && value !== "";
    }).length;

    const progress = totalFields > 0 ? (filledFields / totalFields) * 100 : 0;

    return { progress, filledFields, totalFields };
  };

  const renderProgress = () => {
    const progressData = getProgressData();
    if (!progressData) return null;

    const { progress, filledFields, totalFields } = progressData;

    return (
      <div className="form-progress mb-6">
        <div className="form-progress-bar bg-gray-200 rounded-full h-2 mb-2">
          <div
            className="form-progress-fill bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="form-progress-text text-sm text-gray-600">
          {Math.round(progress)}% Complete ({filledFields}/{totalFields})
        </span>
      </div>
    );
  };

  // Form validation state
  const getHasErrors = () => {
    return FormService.hasErrors(form);
  };

  // Actions renderer
  const renderActions = () => {
    const formClasses = getFormClasses();
    const hasErrors = getHasErrors();

    return (
      <div className={`${formClasses.buttonContainer} flex gap-3 pt-6`}>
        {config.showResetButton && (
          <button
            type="button"
            onClick={handleReset}
            disabled={loading}
            className={`${formClasses.button} flex items-center justify-center gap-2 ${formClasses.resetButton} px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50`}
          >
            {config.resetIcon && <config.resetIcon className="w-4 h-4" />}
            {config.resetButtonText || (config.resetIcon ? null : "Reset")}
          </button>
        )}

        {config.showSubmitButton !== false && (
          <Button
            className={`${formClasses.button} ${formClasses.submitButton} ${
              loading ? formClasses.loading : ""
            }`}
            isLoading={loading}
            disabled={loading || config.disableSubmit}
          >
            {loading ? (
              <>Submitting...</>
            ) : (
              <>
                {config.icon && <config.icon className="w-4 h-4 mr-2" />}
                {config.submitButtonText || "Submit"}
              </>
            )}
          </Button>
        )}
      </div>
    );
  };

  const groupedFields = getGroupedFields();
  const groupedFieldEntries = Object.entries(groupedFields);

  return (
    <form
      onSubmit={form.handleSubmit(handleSubmit)}
      className={getLayoutClasses()}
      style={style}
      noValidate
      autoComplete="off"
    >
      {renderProgress()}

      <div className="form-fields space-y-2">
        {groupedFieldEntries.map(
          ([groupName, [groupFields, groupHelperText]]) =>
            renderFieldGroup(groupName, groupFields, groupHelperText),
        )}
      </div>

      {renderCustomFooter ? renderCustomFooter() : renderActions()}
    </form>
  );
};
